/**
 * Account dropdown in the app-shell header.
 *
 * LLui's standard menu owns open state, keyboard/typeahead navigation,
 * positioning, dismissal, focus return, and trigger ARIA. DungeonLogs supplies
 * the account-specific item composition and navigation messages.
 */
import { button, dropdownMenu, type DropdownMenuOptions } from '@dl/ui';
import {
  derived,
  div,
  each,
  span,
  text,
  type Mountable,
  type Send,
  type Signal,
} from '@llui/dom';

import { t } from '../i18n.js';

export interface UserMenuStateShape {
  session: {
    userEmail: string | null;
    isSystemAdmin: boolean;
    mode: 'gm' | 'player';
  };
}

interface UserMenuMsgFns<Msg> {
  navigateToAccount: () => Msg;
  navigateToSettings: () => Msg;
  navigateToCampaigns: () => Msg;
  navigateToAdmin: () => Msg;
  signOut: () => Msg;
}

function pickEmail(s: UserMenuStateShape): string | null {
  return s.session.userEmail;
}

/** First two letters of the email-local-part, uppercased. */
function initialsOf(email: string | null): string {
  if (!email) return '?';
  const local = email.split('@')[0] ?? email;
  const letters = local.replace(/[^a-zA-Z0-9]/g, '');
  return (letters.slice(0, 2) || '?').toUpperCase();
}

type UserMenuValue = 'account' | 'campaigns' | 'settings' | 'admin' | 'sign-out';

interface UserMenuSnapshot {
  key: string;
  email: string | null;
  isGm: boolean;
  isAdmin: boolean;
}

function userMenuSnapshot(state: UserMenuStateShape): UserMenuSnapshot {
  const email = pickEmail(state);
  const isGm = state.session.mode === 'gm';
  const isAdmin = state.session.isSystemAdmin;
  return {
    key: `${email ?? ''}:${isGm ? 'gm' : 'player'}:${isAdmin ? 'admin' : 'member'}`,
    email,
    isGm,
    isAdmin,
  };
}

function viewMenuForCapabilities<Msg>(
  send: Send<Msg>,
  msgFns: UserMenuMsgFns<Msg>,
  snapshot: UserMenuSnapshot,
): Mountable {
  const items = [
    { value: 'account', kind: 'action' },
    { value: 'campaigns', kind: 'action' },
    ...(snapshot.isGm ? [{ value: 'settings', kind: 'action' } as const] : []),
    ...(snapshot.isAdmin ? [{ value: 'admin', kind: 'action' } as const] : []),
    { value: 'sign-out', kind: 'action' },
  ] satisfies DropdownMenuOptions['items'];

  const itemButton = (
    parts: Parameters<DropdownMenuOptions['children']>[0],
    value: UserMenuValue,
    className: string,
    testid: string,
    label: string,
  ): Mountable =>
    button({
      variant: 'ghost',
      className: `user-menu-item ${className}`,
      testid,
      attrs: { ...parts.item(value).item },
      children: [text(label)],
    });

  return dropdownMenu({
    id: 'user-menu',
    items,
    placement: 'bottom-end',
    panelClass: 'user-menu-panel',
    testid: 'user-menu-popover',
    trigger: (trigger) =>
      button({
        variant: 'icon',
        className: 'user-menu-button',
        testid: 'user-menu-button',
        ariaLabel: t('account.menu.label', { email: snapshot.email ?? '' }),
        attrs: { ...trigger },
        children: [
          span({ class: 'user-menu-avatar', 'aria-hidden': 'true' }, [
            text(initialsOf(snapshot.email)),
          ]),
        ],
      }),
    children: (parts) => [
      div({ class: 'user-menu-list' }, [
        div(
          {
            class: 'user-menu-identity',
            role: 'presentation',
            'data-testid': 'user-menu-email',
          },
          [
            span({ class: 'user-menu-identity-label' }, [text(t('account.menu.signed_in_as'))]),
            span({ class: 'user-menu-identity-email' }, [
              text(snapshot.email ?? ''),
            ]),
          ],
        ),
        itemButton(
          parts,
          'account',
          'user-menu-account',
          'user-menu-account',
          t('account.menu.account'),
        ),
        itemButton(
          parts,
          'campaigns',
          'user-menu-switch-campaign',
          'user-menu-switch-campaign',
          t('campaigns.switcher.manage'),
        ),
        ...(snapshot.isGm
          ? [
              itemButton(
                parts,
                'settings',
                'user-menu-settings',
                'settings-link',
                t('account.menu.settings'),
              ),
            ]
          : []),
        ...(snapshot.isAdmin
          ? [
              itemButton(
                parts,
                'admin',
                'user-menu-admin',
                'user-menu-admin',
                t('account.menu.admin'),
              ),
            ]
          : []),
        itemButton(parts, 'sign-out', 'user-menu-signout', 'sign-out', t('auth.sign_out')),
      ]),
    ],
    onSelect: (value) => {
      switch (value as UserMenuValue) {
        case 'account':
          send(msgFns.navigateToAccount());
          break;
        case 'campaigns':
          send(msgFns.navigateToCampaigns());
          break;
        case 'settings':
          send(msgFns.navigateToSettings());
          break;
        case 'admin':
          send(msgFns.navigateToAdmin());
          break;
        case 'sign-out':
          send(msgFns.signOut());
          break;
      }
    },
  });
}

export function viewUserMenu<Model extends UserMenuStateShape, Msg>(
  state: Signal<Model>,
  send: Send<Msg>,
  msgFns: UserMenuMsgFns<Msg>,
): Mountable {
  return div({ class: 'user-menu', 'data-testid': 'user-menu' }, [
    each(state.map((value) => [userMenuSnapshot(value)]), {
      key: (snapshot) => snapshot.key,
      render: (item) => {
        const snapshot = derived([item], (value) => value).peek();
        return [
          div({ class: 'user-menu-boundary' }, [
            viewMenuForCapabilities(send, msgFns, snapshot),
          ]),
        ];
      },
    }),
  ]);
}
